/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЗЕРКАЛО ЧИСТОЙ ЧАСТИ ГРУПП. ИСТОЧНИК — `src/rk/gruppy.ts`.
 *
 * Проверка имени идёт ДВАЖДЫ: в браузере, чтобы кнопка «Создать» гасла
 * до отправки, и на сервере, потому что браузеру верить нельзя. Два
 * разных набора правил дали бы кнопку, которая горит на имени, которое
 * сервер не примет, — и человек нажимал бы её, пока не сдастся.
 *
 * Сторож `src/rk/gruppy.spec.ts` сверяет оба файла ЗНАК В ЗНАК, кроме
 * этой шапки. Правка здесь без правки там (и наоборот) не проходит.
 * ═══════════════════════════════════════════════════════════════════════
 */

/** Длиннее не поместится в колонку и в метку строки. */
export const ИМЯ_ДО = 40;

export interface Группа {
  id: string;
  name: string;
  /** Сколько кампаний в ней сейчас. */
  count: number;
}

/**
 * Проверить имя группы.
 *
 * ВОЗВРАЩАЕТ СПИСОК БЕД, А НЕ ПЕРВУЮ. Человек, поправивший одно
 * и получивший отказ по второму, справедливо считает, что сервис
 * издевается.
 */
export function проверитьИмя(имя: string, занятые: readonly string[] = []): string[] {
  const беды: string[] = [];
  const чистое = имя.trim();

  if (чистое === '') беды.push('У группы должно быть имя');
  if (чистое.length > ИМЯ_ДО) беды.push(`Имя длиннее ${ИМЯ_ДО} знаков не поместится в колонку`);

  /* ПЕРЕНОС СТРОКИ ВНУТРИ ИМЕНИ НЕ ВИДЕН НИГДЕ, КРОМЕ БАЗЫ.
     В колонке он превратится в пробел, в метке — тоже, а две
     группы «Поиск» и «Поиск\n» станут неразличимы на глаз. */
  if (/[\r\n\t]/.test(имя)) беды.push('В имени группы не бывает переносов строк');

  if (чистое !== '' && занятые.some((з) => сравнимо(з) === сравнимо(чистое))) {
    беды.push(`Группа «${чистое}» уже есть`);
  }

  return беды;
}

/** Имя, приведённое к хранимому виду: без крайних пробелов и двойных внутри. */
export function прибрать(имя: string): string {
  return имя.trim().replace(/\s+/g, ' ');
}

/**
 * Порядок групп в колонке — по имени, а не по времени создания.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОТОМУ ЧТО КОЛОНКУ ЧИТАЮТ ГЛАЗАМИ КАЖДЫЙ ДЕНЬ.
 *
 * По времени создания порядок случайный: новая группа появляется внизу,
 * и через месяц двадцать групп лежат в порядке, который человек не может
 * ни предсказать, ни воспроизвести. По имени он находит нужную,
 * не читая всех.
 *
 * Сравнение русское: `localeCompare` кладёт «Ёлка» между «Егерь»
 * и «Жираф», а побайтовое — в конец алфавита, после «Я».
 */
export function поПорядку(группы: readonly Группа[]): Группа[] {
  return [...группы].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

/** «2 кампании» — как в окне групп. */
export function кампанийСловами(n: number): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return `${n} кампаний`;
  if (b > 1 && b < 5) return `${n} кампании`;
  if (b === 1) return `${n} кампания`;
  return `${n} кампаний`;
}

/**
 * Что сказать под полем новой группы.
 *
 * ДВЕ РАЗНЫЕ ПОДПИСИ, потому что вопрос у человека разный. Пока групп
 * нет, он не знает, зачем они; когда есть — не знает, обязана ли новая
 * быть непустой.
 */
export function подписьНовой(естьГруппы: boolean): string {
  return естьГруппы
    ? 'Пустую группу тоже можно завести: кампании положите потом, выделив их в таблице.'
    : 'Заведите первую — кампании положите потом, выделив их в таблице.';
}

/**
 * Сравнимый вид имени: без регистра и без разницы «е»/«ё».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ДВЕ ГРУППЫ «ПОИСК» И «поиск» — ЭТО ОДНА ГРУППА, НАБРАННАЯ ДВАЖДЫ.
 *
 * И «Ёлка» с «Елка» — тоже: половина клавиатур ставит «е» вместо «ё»
 * молча. Человек, у которого в колонке две одинаковые строки, не может
 * понять, в какой из них его кампании, — и не может это починить,
 * потому что не видит разницы.
 * ═══════════════════════════════════════════════════════════════════════
 */
function сравнимо(имя: string): string {
  return прибрать(имя).toLowerCase().replace(/ё/g, 'е');
}
